// BS3-001: edit an existing 1:1 shift from the Scheduling Board. Pure helpers (tested in __tests__/shiftEditRules.test.js).

export const SUPPORT_RATIOS = ['1:1', '1:2', '1:3', '2:1'];
const CLOSED = ['COMPLETED', 'CANCELLED', 'INVOICED', 'APPROVED', 'BILLED', 'PAID', 'IN_PROGRESS', 'NO_SHOW'];

/** Can this user edit this shift here? { can, reason } — the reason is shown on the disabled button. */
export function canEditShift(shift, permissions = [], { locked = null, group = null } = {}) {
  if (!shift) return { can: false, reason: '' };
  if (!(permissions.includes('roster.update') || permissions.includes('roster.manage'))) return { can: false, reason: 'You need roster update permission' };
  if (CLOSED.includes(String(shift.status || '').toUpperCase())) return { can: false, reason: 'A delivered, cancelled or billed shift cannot be edited' };
  if (locked) return { can: false, reason: 'This shift is locked — use the correction path' };
  if (group || shift.shared_group_id) return { can: false, reason: 'Shared group shifts are changed in the group panel' };
  // SIL house shifts: only the date and start / end time are changed here; the rest stays on the house view
  if (shift.house_id || shift.linked_house_id || ['sil', 'group'].includes(String(shift.support_type || '').toLowerCase())) {
    return { can: true, reason: '', silTimeOnly: true };
  }
  return { can: true, reason: '' };
}

/** Starting form values from the shift (and its current agreement service, when it has one). */
export function initialEditForm(shift, currentPsiId = '') {
  const catalogue = shift?.billing_source === 'FUNDING_PLAN' || !!shift?.no_service_agreement;
  return {
    date: shift?.date || '', start_time: shift?.start_time || '', end_time: shift?.end_time || '',
    participant_id: shift?.participant_id || (shift?.linked_participants || [])[0] || '',
    participant_service_item_id: catalogue ? '' : (currentPsiId || ''), agreement_line_id: '',
    service_id: catalogue ? (shift?.service_catalog_id || '') : '',
    catalogue,
    support_ratio: shift?.support_ratio || '1:1',
    staff_id: (shift?.assigned_staff_ids || [])[0] || shift?.assigned_staff_id || '',
    shift_period: shift?.shift_period || 'AUTO', notes: shift?.notes || '',
  };
}

/** client#2 multi line items: the shift's active segments as editable rows when it has two or more, else [] (one item). */
export function initialItems(rows = [], catalogue = false) {
  const live = (rows || []).filter((r) => r.status !== 'DELETED')
    .sort((a, b) => (Number(a.allocation_order) || 0) - (Number(b.allocation_order) || 0));
  if (live.length < 2) return [];
  return live.map((r, i) => ({
    _key: i === 0 ? 'main' : String(r.id || r._id || i),
    psi: catalogue ? (r.service_catalog_id || '') : (r.participant_service_item_id || ''),
    agreement_line_id: r.agreement_line_id || '', start: r.allocation_start_time || '', end: r.allocation_end_time || '',
  }));
}

const itemsKey = (items) => JSON.stringify((items || []).map((it) => [it.psi, it.start, it.end]));

/** The `items` to send (or null): sent when the rows changed, or the shift moved while it has several items. */
export function itemsPayload(form, items, initialItemsList, initial) {
  const main = form.catalogue ? form.service_id : form.participant_service_item_id;
  const rows = (items || []).map((it, i) => (i === 0 ? { ...it, psi: main } : it));
  const moved = form.date !== initial.date || form.start_time !== initial.start_time || form.end_time !== initial.end_time
    || form.participant_id !== initial.participant_id || form.catalogue !== initial.catalogue || main !== (initial.catalogue ? initial.service_id : initial.participant_service_item_id);
  const wasMulti = (initialItemsList || []).length > 1;
  if (rows.length < 2 && !wasMulti) return null;                         // single item: the plain fields cover it
  if (rows.length > 1 && !moved && itemsKey(rows) === itemsKey(initialItemsList)) return null;
  const list = rows.length > 1 ? rows : [{ psi: main, start: form.start_time, end: form.end_time }];   // back to one item
  return list.map((it) => (form.catalogue
    ? { service_id: it.psi, start_time: it.start, end_time: it.end }
    : { participant_service_item_id: it.psi, start_time: it.start, end_time: it.end }));   // the line is derived from the PSI
}

/** Single-worker change done through the Coverage endpoints: { remove, add } or null. */
export function staffChange(before, after) {
  const b = before || ''; const a = after || '';
  if (a === b) return null;
  return { remove: b || null, add: a || null };
}

/** First problem in words, or null. */
export function validateEditForm(form) {
  if (!form.date) return 'Choose the date';
  if (!form.start_time || !form.end_time) return 'Enter the start and end time';
  if (form.start_time === form.end_time) return 'Start and end time cannot be the same';
  if (!form.participant_id) return 'Select the participant';
  if (form.catalogue ? !form.service_id : !form.participant_service_item_id) return form.catalogue ? 'Select the line item' : 'Select the service';
  return null;   // number of workers is not edited here (as in Create Shift); a 2:1 ratio sets two on the server
}

/** Only what changed — PUT /roster/shifts/{id}/edit keeps everything else. null = nothing changed. */
export function buildEditPayload(shift, form, initial) {
  const out = {};
  if (form.date !== initial.date) out.shift_date = form.date;
  if (form.start_time !== initial.start_time) out.start_time = form.start_time;
  if (form.end_time !== initial.end_time) out.end_time = form.end_time;
  const pidChanged = form.participant_id !== initial.participant_id;
  if (pidChanged) out.participant_id = form.participant_id;
  const modeChanged = form.catalogue !== initial.catalogue;
  if (form.catalogue) {
    if (pidChanged || modeChanged || form.service_id !== initial.service_id) {
      out.service_id = form.service_id;
      out.roster_without_agreement = true;
    }
  } else if (pidChanged || modeChanged || form.participant_service_item_id !== initial.participant_service_item_id) {
    out.participant_service_item_id = form.participant_service_item_id;
    if (form.agreement_line_id) out.agreement_line_id = form.agreement_line_id;
  }
  if (form.support_ratio !== initial.support_ratio) out.support_ratio = form.support_ratio;
  if (form.shift_period !== initial.shift_period) out.shift_period = form.shift_period;   // 'AUTO' = back to auto-classification
  if ((form.notes || '') !== (initial.notes || '')) out.notes = form.notes || '';
  return Object.keys(out).length ? out : null;
}

/** Friendly text for the edit endpoint's refusal codes. */
export function editErrorMessage(err) {
  const d = err?.response?.data?.detail;
  const code = d?.code;
  const map = {
    SEGMENTS_NEED_PLAN_TAB: 'This shift has several service segments — change the participant or service in the Plan tab first.',
    SEGMENTS_OUTSIDE_NEW_TIME: 'A service segment would fall outside the new time — adjust the segments in the Plan tab first.',
    SHIFT_LOCKED: 'This shift has been delivered or processed — use the correction path instead.',
    EDIT_ON_HOUSE_VIEW: 'SIL house shifts are edited on the house view.',
    SIL_TIME_ONLY: 'Only the date and time of a SIL house shift can be changed here.',
    AGREEMENT_EXISTS: 'A resident on this shift has an ACTIVE service agreement on the new date — create the shift with the authorised service instead.',
    ACTIVE_HOURS_OUTSIDE_NEW_TIME: 'A sleep-over active period would fall outside the new time — adjust the active hours first.',
    EDIT_IN_GROUP_PANEL: 'This shift is part of a shared group — change it in the group panel.',
    NO_ACTIVE_AGREEMENT: 'No ACTIVE service agreement covers this participant on that date.',
  };
  return map[code] || d?.message || (typeof d === 'string' ? d : null) || err?.response?.data?.error?.message || 'Could not save the shift';
}
